import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { AppState, Platform } from "react-native";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import * as Application from "expo-application";
import * as Notifications from "expo-notifications";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  notificationKeys,
  useMarkNotificationRead,
} from "@src/packages/care/useNotifications";
import {
  useRegisterNotificationDevice,
  useRevokeNotificationDevice,
} from "./hooks/useNotificationDevices";
import {
  dispatchNotificationNavAction,
  getNotificationNavActionForPayload,
} from "./notification-routing";
import type { PushNotification } from "./usePushNotifications";
import {
  NotificationDeliverabilityContext,
  type NotificationDeliverabilityContextValue,
} from "./NotificationDeliverabilityContext";
import {
  getNotificationDeliverabilityStatus,
  type PushTokenRegistrationState,
} from "./notification-deliverability";
import { usePushNotifications } from "./usePushNotifications";

/**
 * Registers this device's Expo push token with the backend so medication /
 * appointment reminders can be delivered as push notifications.
 *
 * This component also owns the deliverability context consumed by Notification
 * Settings. Permission is inspected on mount but requested only by a
 * contextual notification preference interaction exposed through that context.
 *
 */
export function PushTokenRegistrar({
  children,
  enabled = true,
}: {
  children?: ReactNode;
  enabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const navigation =
    useNavigation<NavigationProp<Record<string, object | undefined>>>();
  const markNotificationRead = useMarkNotificationRead();
  const { mutateAsync: registerDevice } = useRegisterNotificationDevice();
  const { mutateAsync: revokeDevice } = useRevokeNotificationDevice();
  const handledResponseKeys = useRef(new Set<string>());
  const pendingRegistration = useRef<Promise<unknown> | null>(null);
  const registrationSuspended = useRef(false);
  const enabledRef = useRef(enabled);
  const [registrationState, setRegistrationState] =
    useState<PushTokenRegistrationState>("idle");
  const [registrationError, setRegistrationError] = useState<string | null>(
    null,
  );

  const refreshNotifications = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  }, [queryClient]);

  const handleNotificationResponse = useCallback(
    (notification: PushNotification) => {
      if (!enabled) return;
      const content = notification.request.content;
      const rawData =
        content.data && typeof content.data === "object" ? content.data : {};
      const notificationId =
        typeof rawData.notificationId === "string"
          ? rawData.notificationId
          : undefined;
      const responseKey =
        notificationId ??
        notification.request.identifier ??
        JSON.stringify(rawData);

      if (handledResponseKeys.current.has(responseKey)) return;
      handledResponseKeys.current.add(responseKey);
      refreshNotifications();

      const action = getNotificationNavActionForPayload({
        type: rawData.type,
        data: rawData.data ?? rawData,
      });
      if (action && notificationId) {
        markNotificationRead.mutate(notificationId);
      }
      if (action) {
        // Let the response listener finish before dispatching into the root
        // navigator. This also handles taps received during app activation.
        setTimeout(() => dispatchNotificationNavAction(navigation, action), 0);
      }
    },
    [enabled, markNotificationRead, navigation, refreshNotifications],
  );

  const handleNotificationReceived = useCallback(() => {
    if (!enabled) return;
    // A foreground push is the server's signal that both the list and badge
    // may have changed. Invalidate both query families together.
    refreshNotifications();
  }, [enabled, refreshNotifications]);

  const {
    pushToken,
    isLoading: isPushLoading,
    error: pushError,
    permissionState,
    tokenResolutionState,
    requestPermissions,
    refreshPermission,
    openDeviceSettings,
  } = usePushNotifications({
    onNotificationReceived: handleNotificationReceived,
    onNotificationResponse: handleNotificationResponse,
  });
  /**
   * A tap that opened the app from cold fires before the response listener
   * exists: the listener mounts only once sign-in has been restored, so that
   * tap was never handled and the app opened on Home. Ask for it once, when
   * this registrar is first enabled, and clear it so a later remount (signing
   * out and back in) cannot replay an old tap.
   */
  const launchResponseChecked = useRef(false);
  useEffect(() => {
    if (!enabled || launchResponseChecked.current || Platform.OS === "web") {
      return;
    }
    launchResponseChecked.current = true;
    void Notifications.getLastNotificationResponseAsync().then((response) => {
      if (!response) return;
      handleNotificationResponse(response.notification as PushNotification);
      void Notifications.clearLastNotificationResponseAsync();
    });
  }, [enabled, handleNotificationResponse]);

  const { tenantId, patientId } = useApiClient();
  const registeredToken = useRef<string | null>(null);
  const registeredPatientId = useRef<string | null>(null);

  const registerToken = useCallback(
    (token: string) => {
      if (
        !enabled ||
        registrationSuspended.current ||
        !tenantId ||
        !patientId
      ) {
        return;
      }
      if (
        registeredToken.current === token &&
        registeredPatientId.current === patientId
      ) {
        return;
      }

      registeredToken.current = token;
      registeredPatientId.current = patientId;
      setRegistrationState("pending");
      setRegistrationError(null);
      const registration = registerDevice({
        pushToken: token,
        platform: Platform.OS === "ios" ? "ios" : "android",
        appVersion: Application.nativeApplicationVersion ?? undefined,
      });
      pendingRegistration.current = registration;
      void registration
        .then(() => {
          setRegistrationState("registered");
          setRegistrationError(null);
        })
        .catch((error: unknown) => {
          // Do not expose the push token. Keep the error available only for
          // diagnostics while the UI uses a translated generic recovery copy.
          registeredToken.current = null;
          registeredPatientId.current = null;
          setRegistrationState("failed");
          setRegistrationError(
            error instanceof Error
              ? error.message
              : "Push token registration failed",
          );
        })
        .finally(() => {
          if (pendingRegistration.current === registration) {
            pendingRegistration.current = null;
          }
        });
    },
    [enabled, patientId, registerDevice, tenantId],
  );

  const revokeRegisteredDevice = useCallback(async () => {
    registrationSuspended.current = true;
    const registration = pendingRegistration.current;
    if (registration) {
      try {
        await registration;
      } catch {
        // The token is still revoked below when registration failed.
      }
    }

    const token = registeredToken.current ?? pushToken;
    if (!token) {
      setRegistrationState("idle");
      setRegistrationError(null);
      return;
    }

    await revokeDevice(token);
    registeredToken.current = null;
    registeredPatientId.current = null;
    setRegistrationState("idle");
    setRegistrationError(null);
  }, [pushToken, revokeDevice]);

  useEffect(() => {
    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "active") refreshNotifications();
    });
    return () => subscription.remove();
  }, [refreshNotifications]);

  useEffect(() => {
    const becameEnabled = !enabledRef.current && enabled;
    if (becameEnabled) {
      registrationSuspended.current = false;
    }
    enabledRef.current = enabled;

    if (!enabled) {
      registrationSuspended.current = true;
      registeredToken.current = null;
      registeredPatientId.current = null;
      setRegistrationState("idle");
      setRegistrationError(null);
      return;
    }
    if (registrationSuspended.current) return;
    if (!pushToken || !tenantId || !patientId) return;
    registerToken(pushToken);
  }, [enabled, patientId, pushToken, registerToken, tenantId]);

  const retryRegistration = useCallback(() => {
    if (pushToken) {
      registeredToken.current = null;
      registerToken(pushToken);
      return;
    }
    void requestPermissions();
  }, [pushToken, registerToken, requestPermissions]);

  const status = getNotificationDeliverabilityStatus({
    permissionState,
    tokenResolutionState,
    tokenRegistrationState: registrationState,
  });
  const contextValue = useMemo<NotificationDeliverabilityContextValue>(
    () => ({
      status,
      permissionState,
      isLoading: isPushLoading || registrationState === "pending",
      error: registrationError ?? pushError,
      requestPermission: requestPermissions,
      refreshPermission,
      retryRegistration,
      revokeRegisteredDevice,
      openDeviceSettings,
    }),
    [
      isPushLoading,
      openDeviceSettings,
      permissionState,
      pushError,
      refreshPermission,
      registrationError,
      registrationState,
      requestPermissions,
      retryRegistration,
      revokeRegisteredDevice,
      status,
    ],
  );

  return (
    <NotificationDeliverabilityContext.Provider value={contextValue}>
      {children}
    </NotificationDeliverabilityContext.Provider>
  );
}
